import { loadImage } from "client/utils/imageEdit/loadImage";
import { useEffect, useState } from "react";
import { deleteImage, setDimension, setIsCorrupted, setPreview } from "store/features/bulkResize/actions";
import { BulkResizeImage } from "store/features/bulkResize/types";
import { validateResizeDimension } from "client/utils/validateResizeDimension";
import { useAppDispatch, useAppSelector } from "store/store";
import { MAX_RESIZE_SIZE_IN_PX } from "store/features/bulkResize/utils/validateResizeDimensions";
import { convertToPixels } from "client/utils/pixels/convertToPixels";
import { convertFromPixels } from "client/utils/pixels/convertFromPixels";
import { roundFloat } from "client/utils/roundFloat";
import { translateFunc } from "client/utils/translateFunc";
import { downloadImageInFrontendTools } from "client/utils/downloadImageInFrontendTools";

export const useBulkResizerPreviewImage = (image: BulkResizeImage) => {
    const unit = useAppSelector((store) => store.bulkResize.resizeSettings.unit);
    const dimensionType = useAppSelector((store) => store.bulkResize.resizeSettings.dimensionType);
    const byPercentage = useAppSelector((store) => store.bulkResize.resizeSettings.byPercentage);
    const dpi = useAppSelector((store) => store.bulkResize.resizeSettings.dpi);
    const dispatch = useAppDispatch();
    const dimensionsValid = validateResizeDimension(
        image,
        dimensionType,
        unit,
        byPercentage.percentage,
        MAX_RESIZE_SIZE_IN_PX,
        dimensionType === "percentage" || unit === "pixels" ? undefined : +dpi,
    );
    const widthValid =
        convertToPixels(image.preview.width, unit, unit === "pixels" ? undefined : +dpi) <= MAX_RESIZE_SIZE_IN_PX;
    const heightValid =
        convertToPixels(image.preview.height, unit, unit === "pixels" ? undefined : +dpi) <= MAX_RESIZE_SIZE_IN_PX;
    const dimensionsErrorMessage =
        widthValid && heightValid
            ? ""
            : `${translateFunc("maximum-allowed-value-for", {
                  width_or_height:
                      !widthValid && !heightValid
                          ? `${translateFunc("width")} / ${translateFunc("height")}`
                          : !widthValid
                            ? translateFunc("width")
                            : !heightValid
                              ? translateFunc("height")
                              : "",
                  size: roundFloat(
                      convertFromPixels(MAX_RESIZE_SIZE_IN_PX, unit, unit === "pixels" ? undefined : +dpi),
                      false,
                  ),
                  unit: translateFunc(unit),
              })} ${unit !== "pixels" ? translateFunc("at-dpi", { dpi }) : ""}`;
    const src = image.preview.src;

    const handleDelete = () => {
        dispatch(deleteImage(image.id));
    };

    const fetchImageDimensions = async () => {
        let src = image.original.src;
        if (
            image.format === ".heic" ||
            image.format === ".heif" ||
            image.format === "heic" ||
            image.format === "heif"
        ) {
            try {
                const blobRes = await fetch(src);
                if (!blobRes) return;
                const blob = await blobRes.blob();
                if (!blob) return;
                const heic2any = (await import("heic2any")).default;
                const conversionRes = await heic2any({ blob });
                src = URL.createObjectURL(Array.isArray(conversionRes) ? conversionRes[0] : conversionRes);
            } catch (error) {}
        }
        const { error, width, height } = await loadImage(src);
        if (error) {
            const { src, error } = await downloadImageInFrontendTools(
                {
                    type: "url",
                    settings: {
                        url: image.original.src,
                    },
                },
                image.original.src,
                0,
            );
            if (error || !src) {
                handleCorruptImage();
                return;
            }
            dispatch(setPreview({ id: image.id, value: { src } }));
            const { width, height } = await loadImage(src);
            if (width === 0 || height === 0) {
                handleCorruptImage();
                return;
            }
            dispatch(
                setDimension({
                    imageId: image.id,
                    width,
                    height,
                }),
            );
            return;
        }

        if (width === 0 || height === 0) {
            handleCorruptImage();
            return;
        }

        dispatch(setPreview({ id: image.id, value: { src } }));

        dispatch(
            setDimension({
                imageId: image.id,
                width,
                height,
            }),
        );
    };

    function handleCorruptImage() {
        console.log(`${image.title} is corrupted`);
        dispatch(
            setIsCorrupted({
                id: image.id,
                isCorrupted: true,
            }),
        );
    }

    useEffect(() => {
        fetchImageDimensions();
    }, [image.original.src]);

    return { handleDelete, unit, dimensionsValid, dpi, widthValid, heightValid, dimensionsErrorMessage, src };
};
